import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { DEFAULT_LESSONS_PAGE_SIZE } from '@/constant';
import { ILesson } from '@/models/lesson';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import PreviewIcon from '@mui/icons-material/Preview';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, zhCN, zhHK } from '@mui/x-date-pickers/locales';
import { DialogProps } from '@toolpad/core/useDialogs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ClassModel } from '../types/homework';

export type FormValues = {
    name: string;
    lessonId: string;
    lessonName: string;
    class: ClassModel[];
    writingHints: boolean;
    freeStyleMode: boolean;
    startTime: Date | null;
    endTime: Date | null;
    offlineMode: boolean;
};

export interface ExtendedCreateHomeworkDialogProps<P = undefined, R = void> extends DialogProps<P, R> {
    onPreview: (id: string) => Promise<void>;
    onSearch?: (searchQuery: string) => Promise<void>;
}

export function CreateHomeworkDialog({
    payload,
    open,
    onClose,
    onPreview,
    onSearch,
}: ExtendedCreateHomeworkDialogProps<{ data?: { classesData: ClassModel[]; lessons: ILesson[] } }, FormValues | null>) {
    // provider
    const { t, i18n } = useTranslation();

    // Form state
    const initialValues = {
        name: '',
        lessonId: '',
        lessonName: '',
        class: [],
        writingHints: false,
        freeStyleMode: false,
        startTime: null,
        endTime: null,
    };
    const [formValues, setFormValues] = useState<FormValues>({ ...initialValues, offlineMode: false });

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [lessonSelected, setLessonSelected] = useState<ILesson | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [errors, setErrors] = useState<{
        name: string;
        lessonId: string;
        lessonName: string;
        class: string;
        writingHints: string;
        freeStyleMode: string;
        startTime: string;
        endTime: string;
    }>({ name: '', lessonId: '', lessonName: '', class: '', writingHints: '', freeStyleMode: '', startTime: '', endTime: '' });

    const setFieldValue = <T extends keyof FormValues>(field: T, value: FormValues[T]) => {
        setFormValues((prev) => ({ ...prev, [field]: value }));
    };

    // Reset search when dialog opens
    const resetSearch = useCallback(() => {
        setIsSearching(false);
    }, []);

    // Search lessons function
    const performSearch = useCallback(
        async (query: string) => {
            try {
                setIsSearching(true);
                await onSearch?.(query);
            } catch (error) {
                console.error('Error searching lessons:', error);
            } finally {
                setIsSearching(false);
            }
        },
        [onSearch],
    );
    const debouncedPerformSearch = useDebounce(performSearch, 300);

    const getCurrentLanguage = () => {
        if (i18n.language === 'zh-HK') {
            return zhHK.components.MuiLocalizationProvider.defaultProps.localeText;
        }
        if (i18n.language === 'zh-CN') {
            return zhCN.components.MuiLocalizationProvider.defaultProps.localeText;
        }
        if (i18n.language === 'en') {
            return enUS.components.MuiLocalizationProvider.defaultProps.localeText;
        } else {
            return enUS.components.MuiLocalizationProvider.defaultProps.localeText;
        }
    };

    // Validate form
    const validateForm = useCallback(() => {
        const newErrors: {
            name: string;
            lessonId: string;
            lessonName: string;
            class: string;
            writingHints: string;
            freeStyleMode: string;
            startTime: string;
            endTime: string;
        } = { name: '', lessonId: '', lessonName: '', class: '', writingHints: '', freeStyleMode: '', startTime: '', endTime: '' };

        // Name validation
        if (formValues.name && formValues.name.length > 50) {
            newErrors.name = t('homework.error.textLimit');
        }
        // LessonId validation
        if (!formValues.lessonId) {
            newErrors.lessonId = t('homework.error.lessonRequired');
        }
        // Class validation
        if (!formValues.class || formValues.class.length < 1) {
            newErrors.class = t('homework.error.classRequired');
        }
        // StartTime validation
        if (!formValues.startTime) {
            newErrors.startTime = t('homework.error.startTimeRequired');
        }
        // EndTime validation
        if (!formValues.endTime) {
            newErrors.endTime = t('homework.error.endTimeRequired');
        } else if (formValues.startTime && formValues.endTime && new Date(formValues.startTime) >= new Date(formValues.endTime)) {
            newErrors.endTime = t('homework.error.timeRangeError');
        }

        return newErrors;
    }, [formValues, t]);

    const submitForm = useCallback(async () => {
        const newErrors = validateForm();
        if (Object.values(newErrors).some((error) => error)) {
            setErrors(newErrors);
            return;
        }
        setIsSubmitting(true);
        await onClose(formValues);
        setIsSubmitting(false);
    }, [formValues, onClose, validateForm]);

    const dialogOnClose = () => {
        onClose(null); // parent
    };

    useEffect(() => {
        if (open) {
            resetSearch();
        }
    }, [open, resetSearch]);

    return (
        <Dialog onClose={dialogOnClose} open={open && !isSubmitting} fullWidth>
            <DialogTitle>{t('homework.publish.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t('homework.publish.description')}</DialogContentText>
                <DialogContentText>{t('homework.publish.description2')}</DialogContentText>
            </DialogContent>
            <form>
                <DialogContent>
                    <Stack spacing={2}>
                        <TextField
                            name='name'
                            label={`${t('homework.publish.name')} (${t('common.optional')})`}
                            variant='outlined'
                            value={formValues.name}
                            onChange={(e) => {
                                setFieldValue('name', e.target.value);
                            }}
                            error={Boolean(errors.name)}
                            helperText={errors.name ? errors.name : ''}
                        />
                        <Stack direction='row' spacing={2}>
                            <Autocomplete
                                id='lesson-select'
                                sx={{ flex: 1 }}
                                options={payload.data?.lessons || []}
                                getOptionLabel={(option) => option.latestLessonDraft.lessonName}
                                onInputChange={(_, newInputValue) => {
                                    debouncedPerformSearch(newInputValue);
                                }}
                                onChange={(_, newValue) => {
                                    setLessonSelected(newValue);
                                    if (newValue) {
                                        setFieldValue('lessonId', newValue.id);
                                        setFieldValue('lessonName', newValue.latestLessonDraft.lessonName);
                                    } else {
                                        setFieldValue('lessonId', '');
                                        setFieldValue('lessonName', '');
                                    }
                                }}
                                loading={isSearching}
                                slotProps={{
                                    paper: {
                                        sx:
                                            payload.data?.lessons && payload.data?.lessons.length >= DEFAULT_LESSONS_PAGE_SIZE
                                                ? {
                                                      '& .MuiAutocomplete-listbox::after': {
                                                          content: `"${t('homework.useSearchFunction')}"`,
                                                          display: 'block',
                                                          px: 2,
                                                          py: 1,
                                                          borderTop: '1px solid',
                                                          borderColor: 'divider',
                                                          backgroundColor: 'grey.50',
                                                          color: 'text.secondary',
                                                          fontSize: '0.875rem',
                                                          textAlign: 'center',
                                                      },
                                                  }
                                                : {},
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('homework.lessonName')}
                                        placeholder={t('homework.searchLessonsPlaceholder', 'Search lessons...')}
                                        required
                                        error={Boolean(errors.lessonId)}
                                        helperText={errors.lessonId}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isSearching && <CircularProgress color='inherit' size={20} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        {option.latestLessonDraft.lessonName}
                                    </li>
                                )}
                                noOptionsText={isSearching ? t('homework.searchingLessons', 'Searching lessons...') : t('homework.noLessonsFound', 'No lessons found')}
                            />
                            <Button
                                loading={isPreviewing}
                                startIcon={<PreviewIcon />}
                                variant='outlined'
                                disabled={!lessonSelected}
                                onClick={async () => {
                                    if (lessonSelected) {
                                        setIsPreviewing(true);
                                        await onPreview(lessonSelected.id);
                                        setIsPreviewing(false);
                                    }
                                }}
                                loadingIndicator={<CircularProgress size={16} />}
                            >
                                {t('homework.preview.title')}
                            </Button>
                        </Stack>
                        <Autocomplete
                            multiple
                            limitTags={2}
                            id='class-selection-autocomplete'
                            aria-label='class-selection-autocomplete'
                            disableCloseOnSelect
                            value={formValues.class}
                            onChange={(_event, newValue) => {
                                setFieldValue('class', newValue);
                            }}
                            options={payload?.data?.classesData || []}
                            getOptionLabel={(option) => `${option.className} ${option.schoolYear ? ` (${option.schoolYear})` : ''}`}
                            renderOption={(props, option) => {
                                const isSelected = formValues.class.some((selectedItem) => selectedItem.id === option.id);
                                const name = option.className;
                                const avatarColor = generateAvatarColor(name);
                                const bg = avatarColor?.bg;
                                const text = avatarColor?.text;

                                return (
                                    <li {...props} key={props.key}>
                                        <Stack direction='row' spacing={1.5} alignItems='center'>
                                            <Checkbox checked={isSelected} />
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <InitialalsAvatar
                                                    sx={{
                                                        bgcolor: bg,
                                                        color: text,
                                                    }}
                                                >
                                                    {getInitials(name)}
                                                </InitialalsAvatar>
                                                <Typography>
                                                    {option.className}
                                                    {option.schoolYear ? ` (${option.schoolYear})` : ''}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                );
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    name='class'
                                    required
                                    {...params}
                                    label={t('homework.class')}
                                    placeholder={t('homework.class')}
                                    error={!!errors.class}
                                    helperText={errors.class ? errors.class : ''}
                                />
                            )}
                        />
                        <Stack direction={'row'} spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    onChange={(date) => {
                                        if (date && !isNaN(date.getTime())) {
                                            setFieldValue('startTime', date);
                                        }
                                    }}
                                    value={formValues?.startTime || null}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            name: 'startTime',
                                            required: true,
                                            label: t('homework.publish.startTime'),
                                            error: !!errors.startTime,
                                            helperText: errors.startTime ? String(errors.startTime) : '',
                                        },
                                    }}
                                    localeText={getCurrentLanguage()}
                                    enableAccessibleFieldDOMStructure={false}
                                />
                                <DatePicker
                                    onChange={(date) => {
                                        if (date && !isNaN(date.getTime())) {
                                            setFieldValue('endTime', date);
                                        }
                                    }}
                                    value={formValues.endTime || null}
                                    slotProps={{
                                        textField: {
                                            name: 'endTime',
                                            required: true,
                                            fullWidth: true,
                                            label: t('homework.publish.endTime'),
                                            error: !!errors.endTime,
                                            helperText: errors.endTime ? String(errors.endTime) : '',
                                        },
                                    }}
                                    localeText={getCurrentLanguage()}
                                    enableAccessibleFieldDOMStructure={false}
                                />
                            </LocalizationProvider>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button aria-label='dialogue-close' onClick={dialogOnClose} disabled={isSubmitting}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={submitForm} variant='contained' color='primary' loading={isSubmitting}>
                        {t('homework.publish.submit')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
